import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronRight, Search, Shield, Trash2, UserPlus, Users, X } from 'lucide-react';
import { api, type TableEditor, type TableAccessSummaryItem } from '../lib/api';
import { keys } from '../lib/queries';
import {
  R, avatarColor, btnAccent, btnOutline, ft, input as inputStyle, label, mono, shadow,
} from '../lib/ui';
import { Avatar } from './Primitives';
import { Modal } from './Modal';
import { Spinner } from './Spinner';
import { useToast } from './Toast';

export function TableAccessModal({
  docId,
  tableId: initialTableId,
  tableLabel: initialTableLabel,
  onClose,
}: {
  docId: string;
  tableId: string;
  tableLabel: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();

  const [activeTab, setActiveTab] = useState<'current' | 'all'>('current');
  const [currTableId, setCurrTableId] = useState(initialTableId);
  const [currTableLabel, setCurrTableLabel] = useState(initialTableLabel);

  const access = useQuery({
    queryKey: ['table-access', docId, currTableId],
    queryFn: () => api.tableAccess(docId, currTableId),
  });

  const [userSearch, setUserSearch] = useState('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');

  const [roleSearch, setRoleSearch] = useState('');
  const [selectedRole, setSelectedRole] = useState<{ id: string; name: string; color: string | null } | null>(null);
  const [showRolePicker, setShowRolePicker] = useState(false);

  // Загрузка списка ролей сервера Discord
  const discordRolesQuery = useQuery({
    queryKey: ['discord-roles'],
    queryFn: api.discordRoles,
    staleTime: 60_000,
  });
  const serverRoles = discordRolesQuery.data ?? [];

  // Дебаунс поиска пользователей Discord
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedUserSearch(userSearch.trim());
    }, 250);
    return () => clearTimeout(timer);
  }, [userSearch]);

  const searchResults = useQuery({
    queryKey: ['users-search', debouncedUserSearch],
    queryFn: () => api.searchUsers(debouncedUserSearch),
    enabled: debouncedUserSearch.length >= 2,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['table-access', docId, currTableId] });
    qc.invalidateQueries({ queryKey: keys.doc(docId) });
  };

  const addEditor = useMutation({
    mutationFn: (member: { id: string; name: string; avatarUrl: string | null }) =>
      api.tableAddEditor(docId, currTableId, {
        userId: member.id,
      }),
    onSuccess: (editor) => {
      toast(`Пользователь «${editor.name}» назначен редактором листа`, 'ok');
      setUserSearch('');
      setDebouncedUserSearch('');
      invalidate();
    },
    onError: (e) => toast(`Ошибка добавления: ${(e as Error).message}`, 'err'),
  });

  const removeEditor = useMutation({
    mutationFn: (userId: string) => api.tableRemoveEditor(docId, currTableId, userId),
    onSuccess: () => {
      toast('Права редактора листа отозваны', 'ok');
      invalidate();
    },
    onError: (e) => toast(`Ошибка удаления: ${(e as Error).message}`, 'err'),
  });

  const saveRole = useMutation({
    mutationFn: (data: { roleId: string | null; roleName?: string | null }) =>
      api.tableSetEditorRole(docId, currTableId, data),
    onSuccess: (res) => {
      if (res.editorRoleId) {
        toast(`Роль Discord «${res.editorRoleName || res.editorRoleId}» привязана к листу`, 'ok');
      } else {
        toast('Привязка роли Discord снята', 'ok');
      }
      setSelectedRole(null);
      setRoleSearch('');
      setShowRolePicker(false);
      invalidate();
    },
    onError: (e) => toast(`Ошибка настройки роли: ${(e as Error).message}`, 'err'),
  });

  const editors: TableEditor[] = access.data?.editors ?? [];
  const allDocTables: TableAccessSummaryItem[] = access.data?.allDocTables ?? [];
  const members = searchResults.data?.members ?? [];

  // Фильтр списка ролей сервера по поисковому запросу
  const filteredRoles = serverRoles.filter((r) => {
    if (!roleSearch.trim()) return true;
    const q = roleSearch.toLowerCase();
    return r.name.toLowerCase().includes(q) || r.id.includes(q);
  });

  const currentRoleName = access.data?.editorRoleName;
  const currentRoleId = access.data?.editorRoleId;

  return (
    <Modal title={`Управление доступом к листам`} onClose={onClose} width={620}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Вкладки переключения между настройкой листа и сводкой */}
        <div style={{ display: 'flex', borderBottom: ft.hair, gap: 4 }}>
          <button
            onClick={() => setActiveTab('current')}
            style={{
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: activeTab === 'current' ? 600 : 400,
              color: activeTab === 'current' ? R.accentText : R.textMuted,
              borderBottom: `2px solid ${activeTab === 'current' ? R.accent : 'transparent'}`,
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            ⚙️ Лист: «{currTableLabel}»
          </button>
          <button
            onClick={() => setActiveTab('all')}
            style={{
              padding: '8px 14px',
              fontSize: 13,
              fontWeight: activeTab === 'all' ? 600 : 400,
              color: activeTab === 'all' ? R.accentText : R.textMuted,
              borderBottom: `2px solid ${activeTab === 'all' ? R.accent : 'transparent'}`,
              background: 'transparent',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            📋 Сводка по всем листам {allDocTables.length > 0 && `(${allDocTables.length})`}
          </button>
        </div>

        {activeTab === 'all' ? (
          /* ВКЛАДКА 2: Сводка по всем ведомствам/листам */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ fontSize: 12.5, color: R.textMuted }}>
              Полная картина выданных прав по всем листам документа. Нажмите «Настроить», чтобы изменить права:
            </div>

            <div
              style={{
                border: ft.edge,
                borderRadius: 6,
                overflow: 'hidden',
                background: R.bgInput,
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.2fr 1.5fr 1fr 90px',
                  padding: '8px 12px',
                  background: R.bgSubtle,
                  borderBottom: ft.strong,
                  fontSize: 11.5,
                  fontWeight: 600,
                  color: R.textMuted,
                  textTransform: 'uppercase',
                }}
              >
                <div>Лист</div>
                <div>Привязанная роль Discord</div>
                <div>Редакторы</div>
                <div>Действие</div>
              </div>

              {allDocTables.map((t) => {
                const isSelected = t.id === currTableId;
                return (
                  <div
                    key={t.id}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.2fr 1.5fr 1fr 90px',
                      alignItems: 'center',
                      padding: '10px 12px',
                      borderBottom: ft.hair,
                      background: isSelected ? `${R.accent}15` : 'transparent',
                      fontSize: 12.5,
                    }}
                  >
                    <div style={{ fontWeight: 600, color: isSelected ? R.accentText : R.text, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {isSelected && <span style={{ color: R.accent }}>▶</span>}
                      {t.label}
                    </div>

                    <div>
                      {t.editorRoleName || t.editorRoleId ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                          <span style={{ fontSize: 13 }}>🏷️</span>
                          <span style={{ fontWeight: 600, color: R.text }}>
                            {t.editorRoleName || t.editorRoleId}
                          </span>
                        </div>
                      ) : (
                        <span style={{ color: R.textMuted, fontSize: 12 }}>— Не назначена</span>
                      )}
                    </div>

                    <div style={{ color: R.textMuted, fontSize: 12 }}>
                      {t.editorsCount > 0 ? (
                        <span style={{ color: R.accentText, fontWeight: 600 }}>
                          👤 {t.editorsCount} чел.
                        </span>
                      ) : (
                        <span>0 чел.</span>
                      )}
                    </div>

                    <div>
                      <button
                        onClick={() => {
                          setCurrTableId(t.id);
                          setCurrTableLabel(t.label);
                          setActiveTab('current');
                        }}
                        style={{
                          ...btnOutline,
                          padding: '3px 8px',
                          fontSize: 11.5,
                          width: '100%',
                        }}
                      >
                        {isSelected ? 'Выбран' : 'Настроить'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          /* ВКЛАДКА 1: Настройка текущего выбранного листа */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {/* БЛОК РОЛИ DISCORD: Текущее состояние */}
            <div
              style={{
                border: ft.edge,
                borderRadius: 6,
                padding: '14px 16px',
                background: currentRoleId ? `${R.accent}11` : R.bgInput,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ ...label, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Users size={14} style={{ color: currentRoleId ? R.accent : R.textMuted }} /> Роль Discord для листа «{currTableLabel}»
                </div>
                {currentRoleId && (
                  <button
                    onClick={() => saveRole.mutate({ roleId: null, roleName: null })}
                    disabled={saveRole.isPending}
                    style={{
                      ...btnOutline,
                      padding: '3px 8px',
                      fontSize: 11.5,
                      color: R.danger,
                      borderColor: `${R.danger}55`,
                    }}
                  >
                    Снять роль
                  </button>
                )}
              </div>

              {currentRoleId ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 12px',
                      background: R.bgPanel,
                      border: `1px solid ${R.accent}55`,
                      borderRadius: 4,
                    }}
                  >
                    <span style={{ fontSize: 16 }}>🏷️</span>
                    <div>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: R.text }}>
                        {currentRoleName || 'Роль без названия'}
                      </div>
                      <div style={{ fontSize: 11, color: R.textMuted, fontFamily: mono }}>
                        ID: {currentRoleId}
                      </div>
                    </div>
                  </div>
                  <span style={{ fontSize: 12, color: R.textMuted }}>
                    ✓ Все участники Discord с этой ролью могут редактировать этот лист.
                  </span>
                </div>
              ) : (
                <div style={{ fontSize: 12, color: R.textMuted }}>
                  К этому листу пока не привязана роль. Выберите нужную роль из списка ниже:
                </div>
              )}

              {/* Выбор роли из сервера Discord */}
              <div style={{ marginTop: 12, position: 'relative' }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <div style={{ position: 'relative', flex: 1 }}>
                    <Search
                      size={14}
                      style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: R.textMuted }}
                    />
                    <input
                      type="text"
                      value={roleSearch}
                      onFocus={() => setShowRolePicker(true)}
                      onChange={(e) => {
                        setRoleSearch(e.target.value);
                        setShowRolePicker(true);
                      }}
                      placeholder="Поиск роли Discord (например: Судья, Прокурор, Адвокат)..."
                      style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', paddingLeft: 30 }}
                    />
                  </div>

                  {selectedRole && (
                    <button
                      onClick={() =>
                        saveRole.mutate({
                          roleId: selectedRole.id,
                          roleName: selectedRole.name,
                        })
                      }
                      disabled={saveRole.isPending}
                      style={{ ...btnAccent, padding: '6px 14px', fontSize: 12 }}
                    >
                      {saveRole.isPending ? 'Привязка…' : `Привязать «${selectedRole.name}»`}
                    </button>
                  )}
                </div>

                {/* Выпадающий список ролей сервера */}
                {showRolePicker && (
                  <div
                    className="rt-scroll"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      maxHeight: 200,
                      overflowY: 'auto',
                      background: R.bgPanel,
                      border: ft.strong,
                      boxShadow: shadow.panel,
                      zIndex: 250,
                      borderRadius: 4,
                    }}
                  >
                    <div style={{ padding: '6px 10px', background: R.bgSubtle, fontSize: 11, color: R.textMuted, borderBottom: ft.hair }}>
                      Роли сервера Discord ({filteredRoles.length} найдено):
                    </div>
                    {filteredRoles.length === 0 ? (
                      <div style={{ padding: 12, fontSize: 12, color: R.textMuted, textAlign: 'center' }}>
                        Роли не найдены
                      </div>
                    ) : (
                      filteredRoles.slice(0, 30).map((r) => {
                        const isCurrent = r.id === currentRoleId;
                        const isChosen = selectedRole?.id === r.id;
                        return (
                          <div
                            key={r.id}
                            onClick={() => {
                              setSelectedRole(r);
                              setRoleSearch(r.name);
                              setShowRolePicker(false);
                            }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              padding: '7px 12px',
                              borderBottom: ft.hair,
                              cursor: 'pointer',
                              background: isChosen ? R.accentSubtle : isCurrent ? `${R.accent}11` : 'transparent',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <span
                                style={{
                                  width: 10,
                                  height: 10,
                                  borderRadius: '50%',
                                  background: r.color || '#888',
                                  display: 'inline-block',
                                }}
                              />
                              <span style={{ fontSize: 13, fontWeight: 600, color: R.text }}>
                                {r.name}
                              </span>
                              {isCurrent && (
                                <span style={{ fontSize: 10, padding: '1px 4px', background: R.accent, color: '#fff', borderRadius: 2 }}>
                                  Текущая
                                </span>
                              )}
                            </div>
                            <span style={{ fontSize: 11, color: R.textMuted, fontFamily: mono }}>
                              {r.id}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* БЛОК ПЕРСОНАЛЬНЫХ ПОЛЬЗОВАТЕЛЕЙ */}
            <div>
              <div style={{ ...label, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <UserPlus size={14} /> Назначить персонального редактора (пользователь)
              </div>

              <div style={{ position: 'relative' }}>
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Введите Discord ID, ник или логин сотрудника…"
                  style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                />

                {userSearch.length > 0 && (
                  <button
                    onClick={() => {
                      setUserSearch('');
                      setDebouncedUserSearch('');
                    }}
                    title="Очистить"
                    style={{
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'transparent',
                      border: 'none',
                      color: R.textMuted,
                      cursor: 'pointer',
                    }}
                  >
                    <X size={14} />
                  </button>
                )}

                {/* Результаты поиска участников */}
                {debouncedUserSearch.length >= 2 && (
                  <div
                    className="rt-scroll"
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: 0,
                      right: 0,
                      maxHeight: 220,
                      overflowY: 'auto',
                      background: R.bgPanel,
                      border: ft.strong,
                      boxShadow: shadow.panel,
                      zIndex: 200,
                      borderRadius: 4,
                    }}
                  >
                    {searchResults.isLoading ? (
                      <div style={{ padding: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontSize: 12, color: R.textMuted }}>
                        <Spinner /> Поиск участников…
                      </div>
                    ) : members.length === 0 ? (
                      <div style={{ padding: 12, fontSize: 12, color: R.textMuted, textAlign: 'center' }}>
                        Участники не найдены
                      </div>
                    ) : (
                      members.map((m: any) => {
                        const alreadyAdded = editors.some((ed) => ed.userId === m.id);
                        return (
                          <div
                            key={m.id}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 10,
                              padding: '8px 12px',
                              borderBottom: ft.hair,
                            }}
                          >
                            <Avatar name={m.name} src={m.avatarUrl} size={28} color={avatarColor(m.id)} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600, color: R.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {m.name}
                              </div>
                              <div style={{ fontSize: 11, color: R.textMuted, fontFamily: mono }}>
                                @{m.username} · ID: {m.discordId || m.id}
                              </div>
                            </div>

                            {alreadyAdded ? (
                              <span style={{ fontSize: 11, color: R.accent, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Check size={13} /> Добавлен
                              </span>
                            ) : (
                              <button
                                onClick={() => addEditor.mutate(m)}
                                disabled={addEditor.isPending}
                                style={{ ...btnAccent, padding: '3px 10px', fontSize: 12 }}
                              >
                                Назначить
                              </button>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>

              {/* Список текущих персональных редакторов */}
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: 11.5, color: R.textMuted, marginBottom: 6 }}>
                  Текущие назначенные редакторы листа ({editors.length}):
                </div>

                {access.isLoading ? (
                  <div style={{ padding: 12, textAlign: 'center' }}><Spinner /></div>
                ) : editors.length === 0 ? (
                  <div style={{ padding: '10px 12px', background: R.bgInput, border: ft.edge, borderRadius: 4, fontSize: 12, color: R.textMuted, textAlign: 'center' }}>
                    Персональные редакторы пока не добавлены
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {editors.map((ed) => (
                      <div
                        key={ed.userId}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '6px 10px',
                          background: R.bgInput,
                          border: ft.edge,
                          borderRadius: 4,
                        }}
                      >
                        <Avatar name={ed.name} src={ed.avatarUrl} size={26} color={avatarColor(ed.userId)} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 600, color: R.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {ed.name}
                          </div>
                          <div style={{ fontSize: 10.5, color: R.textMuted, fontFamily: mono }}>
                            Логин: {ed.login ?? '—'}
                          </div>
                        </div>

                        <span style={{ fontSize: 11, padding: '2px 6px', background: R.accentSubtle, color: R.accentText, fontWeight: 600 }}>
                          Редактор
                        </span>

                        <button
                          onClick={() => removeEditor.mutate(ed.userId)}
                          disabled={removeEditor.isPending}
                          title="Отозвать права"
                          style={{
                            display: 'grid',
                            placeItems: 'center',
                            width: 26,
                            height: 26,
                            color: R.danger,
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                          }}
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
